import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterBar, { type FilterState } from "@/components/feature/FilterBar";
import WorkCard from "@/components/feature/WorkCard";
import { categoryMeta, categoryList } from "@/mocks/categories";
import { applyFilter, getWorksByType, sortWorks } from "@/utils/media";
import { usePageSeo } from "@/hooks/usePageSeo";

const PAGE_SIZE = 12;

const defaultFilter: FilterState = {
  region: "全部",
  year: "全部",
  language: "全部",
  genre: "全部",
  sort: "hot",
};

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const meta = type ? categoryMeta[type] : undefined;

  const [filter, setFilter] = useState<FilterState>(defaultFilter);
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    setFilter(defaultFilter);
    setVisible(PAGE_SIZE);
  }, [type]);

  const baseList = useMemo(() => (type ? getWorksByType(type) : []), [type]);
  const resultList = useMemo(
    () => sortWorks(applyFilter(baseList, filter), filter.sort),
    [baseList, filter],
  );

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [filter]);

  const seoTitle = meta ? `${meta.label} - 91av视频影视资源分类` : "影视分类 - 91av视频";

  usePageSeo({
    title: seoTitle,
    description: meta
      ? `${meta.intro} 在 91av视频 ${meta.label}频道按地区、年份、语言与题材筛选，快速浏览最新影视资源。`
      : "91av视频影视分类页，按电影、电视剧、综艺等类型浏览影视资源。",
    keywords: `91av视频,${meta?.label ?? "影视"}分类,影视资源,在线视频`,
  });

  if (!meta || !type) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 py-20 text-center md:px-8">
        <i className="ri-emotion-sad-line text-5xl text-foreground-400" />
        <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">未找到该分类</h1>
        <p className="mt-2 text-sm text-foreground-600">你访问的影视分类不存在或已下线。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50"
        >
          <i className="ri-home-4-line" />
          返回首页
        </Link>
      </div>
    );
  }

  const patch = (next: Partial<FilterState>) => setFilter((prev) => ({ ...prev, ...next }));
  const reset = () => setFilter(defaultFilter);
  const shown = resultList.slice(0, visible);

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-8 md:py-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: meta.label }]} />

      <div className="mt-5 flex flex-col gap-4 rounded-lg border border-background-200/70 bg-background-100/60 p-6 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {meta.label}频道
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">{meta.intro}</p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          {categoryList.map((cat) => (
            <Link
              key={cat.type}
              to={`/category/${cat.type}`}
              className={`flex items-center gap-1 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                cat.type === type
                  ? "border-primary-300 bg-primary-100 text-primary-700"
                  : "border-background-300/70 bg-background-50 text-foreground-700 hover:border-primary-300"
              }`}
            >
              <i className={cat.icon} />
              {cat.label}
            </Link>
          ))}
        </div>
      </div>

      <div className="mt-5">
        <FilterBar value={filter} onChange={patch} onReset={reset} resultCount={resultList.length} />
      </div>

      {shown.length > 0 ? (
        <>
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5 xl:grid-cols-6">
            {shown.map((work, i) => (
              <WorkCard key={work.id} work={work} rank={filter.sort === "hot" ? i + 1 : undefined} />
            ))}
          </div>

          {visible < resultList.length && (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={() => setVisible((v) => v + PAGE_SIZE)}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 bg-background-50 px-6 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-add-line" />
                加载更多（剩余 {resultList.length - visible} 部）
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="mt-8 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
          <i className="ri-search-eye-line text-5xl text-foreground-400" />
          <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
            没有符合筛选条件的作品
          </h2>
          <p className="mt-2 text-sm text-foreground-600">
            试着放宽地区、年份或题材条件，或重置全部筛选。
          </p>
          <button
            type="button"
            onClick={reset}
            className="mt-6 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-refresh-line" />
            重置筛选
          </button>
        </div>
      )}

      <p className="mt-10 text-center text-xs text-foreground-500">
        当前分类共收录 {baseList.length} 部演示作品，全部数据均为虚构内容。
      </p>
    </div>
  );
}